import { useEffect, useRef, useState, type FormEvent, type ReactElement, type ReactNode } from 'react';
import EmberFlame from '../../../../../shared-web/EmberFlame';
import { GoogleG } from '../../../../../shared-web/google-button';
import { consumeReauthLanding, googleReauthHref, type ReauthLanding } from '../../../../../shared-web/google-signin';
import { trackFailed } from '../../../../../shared-web/track';
import { apiErrorCode, emberApi } from '../api';
import { Link } from './Link';
import {
  markOpenedThisSession, removeLegacyLock, verifyLegacyPassword, webReauthMethods,
  type NotebookLockState,
} from '../notebook-lock';
import './Notebook.css';

/**
 * The cover of My Embers (§4.12, 2026-09-04 拍板).
 *
 * A person's Embers are not a feed of records to administer; they are the
 * things they have lived and said out loud. So the page they live behind is a
 * book that has to be opened — closed on arrival, opened by its owner, and
 * only then the pages. The ceremony runs once a browser session: the second
 * visit of an afternoon lands straight on the pages.
 *
 * The opening is small and quiet — a cover lifting, the pages settling in —
 * because this is a person's own notebook, not a reveal (2026-09-04 second
 * pass: the light sweep and the long approach were cut). The cover asks
 * nothing on its own: a notebook nobody has locked simply opens. Locking is
 * the same gesture as closing (2026-09-05): the corner of the open notebook
 * says `Close the notebook`, and a notebook without a password gets one on
 * the way shut — so `closing` mounts this cover in its open pose and swings it
 * to rest, the opening played backwards.
 *
 * The lock is the account's now (2026-09-07): the password is verified on the
 * server, so a notebook closed on a phone is closed on a laptop too — and
 * `Forgot it?` texts the person's own phone a link back in, rather than
 * offering to take the lock off, which is what it did while the password lived
 * in one browser and could not be recovered. It still guards this page and not
 * the Embers themselves — the account session does that — and every line here
 * says so.
 *
 * The one exception is a lock set before 2026-09-07, which exists only in this
 * browser's storage: it is verified here, quietly becomes the account's lock
 * the first time its owner types it, and keeps the old take-it-off way out
 * until then, because there is nowhere to text a link for a password that was
 * never on the server.
 *
 * Confirm it's you (2026-10-19 Riley): `Forgot it?` first offers to sign in
 * again with the account's own method — Google, or a code to its phone — and
 * then a new password. Every account has one, phone or not, so the texted link
 * is now the second way, kept for those it reaches.
 *
 * The lock state itself is owned by MeShell — the corner, the cover and the
 * row at the foot all read and change the same one, so none of them can hold a
 * stale copy while another takes it off.
 */

/** The cover's swing plus its fade, either direction. Must match the CSS. */
const OPEN_MS = 440;

export interface NotebookProps {
  state: NotebookLockState;
  onLockChange: (state: NotebookLockState) => void;
  onOpen: () => void;
  /** Mount closing: the cover arrives open and swings shut before it asks. */
  closing?: boolean;
}

export function Notebook({ state, onLockChange, onOpen, closing = false }: NotebookProps): ReactElement | null {
  const reducedMotion = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

  const [opening, setOpening] = useState(false);
  const [shutting, setShutting] = useState(closing && !reducedMotion);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [forgot, setForgot] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState('');
  const [sendError, setSendError] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Back from Google's Confirm it's you (a full-page round trip): the answer
  // rides the hash, read once.
  const [landing] = useState<ReauthLanding>(() => consumeReauthLanding());
  useEffect(() => { if (landing) setForgot(true); }, [landing]);
  const reauthMethods = webReauthMethods(state);

  // A legacy lock only stands while the account has none: once the password is
  // handed over, the account's lock is the one that answers.
  const legacy = !state.locked ? state.legacy : null;
  const asking = state.locked || legacy != null;

  const open = (): void => {
    markOpenedThisSession();
    if (reducedMotion) { onOpen(); return; }
    setOpening(true);
  };

  // An unlocked notebook still opens — it just does not ask anything first.
  useEffect(() => {
    if (!asking && !opening) open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asking]);

  useEffect(() => {
    if (!opening) return undefined;
    const t = window.setTimeout(onOpen, OPEN_MS);
    return () => window.clearTimeout(t);
  }, [opening, onOpen]);

  useEffect(() => {
    if (!shutting) return undefined;
    const t = window.setTimeout(() => setShutting(false), OPEN_MS);
    return () => window.clearTimeout(t);
  }, [shutting]);

  useEffect(() => {
    if (asking && !shutting) inputRef.current?.focus();
  }, [asking, shutting]);

  const refused = (): void => {
    setError("That's not it. Try again.");
    setDraft('');
    // The tap itself is the attempt (data-log on the submit); this is the
    // half of it the person actually felt — locked out of their own page.
    trackFailed('notebook_unlock', 'wrong_password');
  };

  /**
   * The legacy lock's one and only migration: verified here against the digest
   * this browser holds, then sent up as the account's password so the next
   * browser meets the same cover. If that send fails the notebook still opens
   * — the person typed their password, and holding their own pages hostage to
   * a failed request would be the wrong half to be strict about — and the
   * local lock stays, so the migration is simply tried again next time.
   */
  const submitLegacy = (password: string): void => {
    verifyLegacyPassword(legacy!, password)
      .then((ok) => {
        if (!ok) { refused(); setBusy(false); return; }
        emberApi.setNotebookLock({ password, source: 'migrated' })
          .then(() => {
            removeLegacyLock();
            onLockChange({ ...state, locked: true, legacy: null });
          })
          .catch(() => { /* the lock stays local; next open tries again */ })
          .finally(() => { setBusy(false); open(); });
      })
      .catch(() => { setBusy(false); });
  };

  const submitPassword = (e: FormEvent): void => {
    e.preventDefault();
    if (busy || !draft) return;
    setBusy(true);
    setError('');
    if (legacy) { submitLegacy(draft); return; }
    emberApi.openNotebook(draft)
      .then(() => { setBusy(false); open(); })
      .catch((err: unknown) => {
        setBusy(false);
        const code = apiErrorCode(err);
        if (code === 'rate_limited') {
          setError('Too many tries. Wait a few minutes, or use Forgot it?');
          setDraft('');
          trackFailed('notebook_unlock', 'rate_limited');
          return;
        }
        if (code === 'wrong_password') { refused(); return; }
        setError('That didn’t go through. Try again.');
        trackFailed('notebook_unlock', 'request_failed');
      });
  };

  /** `Forgot it?` — one text to this account's own phone. */
  const sendLink = (): void => {
    if (sending) return;
    setSending(true);
    setSendError('');
    emberApi.recoverNotebookLock()
      .then((res) => { setSentTo(res.hint || ''); })
      .catch((err: unknown) => {
        const code = apiErrorCode(err);
        setSendError(
          code === 'rate_limited'
            ? 'A link is already on its way. Give it a minute before asking for another.'
            : code === 'no_handle' || code === 'opted_out'
              ? 'We can’t text this account right now.'
              : 'That text didn’t go out. Try again in a moment.',
        );
        trackFailed('notebook_recover', code || 'request_failed');
      })
      .finally(() => setSending(false));
  };

  // Nothing to show: an unlocked notebook with motion turned off is already
  // open by the time the first frame would paint.
  if (!asking && reducedMotion) return null;

  const motion = opening ? ' is-opening' : shutting ? ' is-closing' : '';

  return (
    <div className={`notebook${motion}`}>
      <div className="notebook-stage" aria-hidden={opening || undefined}>
        <div className="notebook-book">
          <div className="notebook-leaves" />
          <div className="notebook-cover">
            <span className="notebook-spine" aria-hidden />
            <EmberFlame className="notebook-emblem" />
            <span className="notebook-name">Your notebook</span>
            <span className="notebook-sub">What you’ve said and kept here</span>
          </div>
        </div>
      </div>

      {!opening && !shutting && asking && (
        <div className="notebook-ask" data-log-seen="notebook_seen" data-log-state="locked">
          <h1 className="notebook-ask-title">Your notebook is closed</h1>
          <form className="notebook-form" onSubmit={submitPassword}>
            <input
              ref={inputRef}
              className="notebook-input"
              type="password"
              value={draft}
              onChange={(e) => { setDraft(e.target.value); setError(''); }}
              placeholder="Password"
              autoComplete="current-password"
              aria-label="Notebook password"
            />
            <button
              type="submit"
              className="ember-btn ember-btn-primary"
              disabled={!draft || busy}
              data-log="notebook_unlock"
            >
              Open
            </button>
          </form>
          {error && <p className="notebook-error" role="alert">{error}</p>}
          <button
            type="button"
            className="notebook-skip ember-text-action"
            aria-expanded={forgot}
            data-log="notebook_forgot"
            data-log-state={legacy ? 'legacy' : reauthMethods.length > 0 ? 'reauth' : state.recovery.available ? 'text' : 'no_phone'}
            onClick={() => setForgot((v) => !v)}
          >
            Forgot it?
          </button>
          {forgot && (
            <div className="notebook-forgot">
              {legacy ? (
                /* A lock from before the account held one. It never left this
                   browser, so there is nothing to send — and nothing to lose
                   by lifting it, which is the way out it has always had. */
                <>
                  <p>
                    You set this password in this browser before locks moved to
                    your account, so there’s nothing to send you. You can take it
                    off and set a new one — your Embers are untouched either way.
                  </p>
                  <button
                    type="button"
                    className="ember-btn ember-btn-secondary"
                    data-log="notebook_lock_removed"
                    data-log-state="forgot"
                    onClick={() => { removeLegacyLock(); onLockChange({ ...state, legacy: null }); }}
                  >
                    Take the lock off
                  </button>
                </>
              ) : sentTo ? (
                <p>
                  Sent to {sentTo}. Open the link and you can set a new password.
                  It works once, for the next 30 minutes.
                </p>
              ) : reauthMethods.length > 0 ? (
                <ConfirmItsYou
                  methods={reauthMethods}
                  phoneHint={state.reauth.phone_hint}
                  landing={landing}
                  onReset={() => { onLockChange({ ...state, locked: true }); open(); }}
                  orElse={state.recovery.available ? (
                    <button
                      type="button"
                      className="ember-text-action"
                      disabled={sending}
                      data-log="notebook_recover"
                      data-log-state="cover"
                      onClick={sendLink}
                    >
                      {sending ? 'Sending…' : 'Text me a link instead'}
                    </button>
                  ) : null}
                  orElseError={sendError}
                />
              ) : state.recovery.available ? (
                <>
                  <p>
                    We’ll text {state.recovery.hint || 'your phone'} a link that
                    opens your notebook and lets you set a new password.
                  </p>
                  <button
                    type="button"
                    className="ember-btn ember-btn-secondary"
                    disabled={sending}
                    data-log="notebook_recover"
                    data-log-state="cover"
                    onClick={sendLink}
                  >
                    {sending ? 'Sending…' : 'Text me a link'}
                  </button>
                  {sendError && <p className="notebook-error" role="alert">{sendError}</p>}
                </>
              ) : state.reauth.methods.length > 0 ? (
                <p>Open the Ember app and tap Forgot it? there.</p>
              ) : state.recovery.reason === 'opted_out' ? (
                <p>
                  You asked us to stop texting you, so we can’t send a link.
                  Text your Ember line anything at all and we can again.
                </p>
              ) : (
                <p>
                  This account has no phone connected, so there’s nowhere to
                  send a link. Connect one in{' '}
                  <Link to="/settings" className="ember-text-action">Settings</Link>
                  {' '}— turn on Text messages — and the way back in is a text away.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Confirm it's you, then a new password (2026-10-19 Riley). Google is a full
 * page round trip (the web's Google door is a redirect flow): it comes back
 * with the session stamped and lands straight on the new password. A phone
 * code goes to the account's own number — the edge picks it, the person sees
 * its last four. The proof lasts five minutes and is spent by the reset.
 */
function ConfirmItsYou({ methods, phoneHint, landing, onReset, orElse, orElseError }: {
  methods: Array<'google' | 'phone'>;
  phoneHint: string;
  landing: ReauthLanding;
  onReset: () => void;
  /** The texted link, offered under the methods while they are on screen. */
  orElse: ReactNode;
  orElseError: string;
}): ReactElement {
  const [step, setStep] = useState<'choose' | 'code' | 'new'>(landing === 'google' ? 'new' : 'choose');
  const [challenge, setChallenge] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(
    landing === 'not_this_account' ? 'That Google account isn’t this one.'
      : landing === 'rate_limited' ? 'Too many tries. Wait a few minutes.'
        : '',
  );
  const failed = (reason: string): void => { trackFailed('notebook_reauth', reason || 'request_failed'); };

  const sendCode = (): void => {
    if (busy) return;
    setBusy(true);
    setError('');
    emberApi.reauthPhoneStart()
      .then((r) => { setChallenge(r.challenge_id); setCode(''); setStep('code'); })
      .catch((err: unknown) => {
        const reason = apiErrorCode(err);
        setError(
          reason === 'resend_too_soon' ? 'A code was just sent. Give it a minute.'
            : reason === 'too_many_requests' ? 'Too many codes for now. Try again later.'
              : 'That code didn’t go out. Try again.',
        );
        failed(reason);
      })
      .finally(() => setBusy(false));
  };

  const checkCode = (e: FormEvent): void => {
    e.preventDefault();
    const digits = code.replace(/\D/g, '');
    if (busy || digits.length !== 6) return;
    setBusy(true);
    setError('');
    emberApi.reauthPhoneVerify(challenge, digits)
      .then(() => { setStep('new'); })
      .catch((err: unknown) => {
        const reason = apiErrorCode(err);
        if (reason === 'code_expired' || reason === 'too_many_attempts') {
          setStep('choose');
          setError('That code expired. Send a new one.');
        } else {
          setError(reason === 'code_incorrect' ? 'That code didn’t match.' : 'That didn’t go through. Try again.');
        }
        setCode('');
        failed(reason);
      })
      .finally(() => setBusy(false));
  };

  const reset = (e: FormEvent): void => {
    e.preventDefault();
    if (busy || !password.trim()) return;
    setBusy(true);
    setError('');
    emberApi.resetNotebookLock(password)
      .then(() => { onReset(); })
      .catch((err: unknown) => {
        const reason = apiErrorCode(err);
        if (reason === 'reauth_required') {
          setStep('choose');
          setError('That took too long. Confirm it’s you again.');
        } else {
          setError(reason === 'password_too_short' ? 'A little longer — at least four characters.' : 'That didn’t go through. Try again.');
        }
        trackFailed('notebook_lock_set', reason || 'request_failed');
        setBusy(false);
      });
  };

  if (step === 'new') {
    return (
      <>
        <p>Set a new password.</p>
        <form className="notebook-form" onSubmit={reset}>
          <input
            className="notebook-input"
            type="password"
            value={password}
            autoFocus
            onChange={(e) => { setPassword(e.target.value); setError(''); }}
            placeholder="New password"
            autoComplete="new-password"
            aria-label="New notebook password"
          />
          <button
            type="submit"
            className="ember-btn ember-btn-primary"
            disabled={busy || !password.trim()}
            data-log="notebook_lock_set"
            data-log-state="reauth"
          >
            Set it
          </button>
        </form>
        {error && <p className="notebook-error" role="alert">{error}</p>}
      </>
    );
  }

  if (step === 'code') {
    return (
      <>
        <p>Enter the code we texted to {phoneHint || 'your phone'}.</p>
        <form className="notebook-form" onSubmit={checkCode}>
          <input
            className="notebook-input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            autoFocus
            onChange={(e) => { setCode(e.target.value); setError(''); }}
            placeholder="6-digit code"
            aria-label="Code"
          />
          <button
            type="submit"
            className="ember-btn ember-btn-primary"
            disabled={busy || code.replace(/\D/g, '').length !== 6}
            data-log="notebook_reauth_code"
          >
            Confirm
          </button>
        </form>
        {error && <p className="notebook-error" role="alert">{error}</p>}
      </>
    );
  }

  return (
    <>
      <p>Confirm it’s you, then set a new password.</p>
      <div className="notebook-reauth-methods">
        {methods.includes('google') && (
          <button
            type="button"
            className="ember-btn ember-btn-secondary notebook-reauth-google"
            disabled={busy}
            data-log="notebook_reauth"
            data-log-state="google"
            onClick={() => {
              window.location.assign(googleReauthHref(window.location.pathname + window.location.search));
            }}
          >
            <GoogleG size={18} />
            Continue with Google
          </button>
        )}
        {methods.includes('phone') && (
          <button
            type="button"
            className="ember-btn ember-btn-secondary"
            disabled={busy}
            data-log="notebook_reauth"
            data-log-state="phone"
            onClick={sendCode}
          >
            {busy ? 'Sending…' : `Text a code to ${phoneHint || 'my phone'}`}
          </button>
        )}
      </div>
      {error && <p className="notebook-error" role="alert">{error}</p>}
      {orElse}
      {orElseError && <p className="notebook-error" role="alert">{orElseError}</p>}
    </>
  );
}

/**
 * The lock's second row (§4.12): quiet text actions at the foot of /me, only
 * once the notebook has a password — change it, or take it off. Putting one
 * on is `Close the notebook` in the corner (2026-09-05): a notebook is locked
 * by closing it, not by a setting, so this row never offers a first password.
 *
 * Both actions ask for the password that is on it (2026-09-07). They used to
 * be one tap each, which was the same hole the cover had: anybody holding an
 * unlocked phone could change or lift the lock without knowing it. A person
 * who cannot produce it uses the cover's `Forgot it?` and the text it sends.
 */
export function NotebookLockRow({ state, onLockChange }: {
  state: NotebookLockState;
  onLockChange: (state: NotebookLockState) => void;
}): ReactElement | null {
  const [mode, setMode] = useState<'' | 'change' | 'remove'>('');
  const [current, setCurrent] = useState('');
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (!state.locked) return null;

  const reset = (): void => { setMode(''); setCurrent(''); setDraft(''); setError(''); };

  const onFailure = (err: unknown): void => {
    const code = apiErrorCode(err);
    setError(
      code === 'wrong_password' ? 'That’s not the password on this notebook.'
        : code === 'rate_limited' ? 'Too many tries. Wait a few minutes.'
          : code === 'password_too_short' ? 'A little longer — at least four characters.'
            : 'That didn’t go through. Try again.',
    );
    trackFailed(mode === 'remove' ? 'notebook_lock_removed' : 'notebook_lock_set', code || 'request_failed');
  };

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (busy || !current.trim()) return;
    if (mode === 'change' && !draft.trim()) return;
    setBusy(true);
    setError('');
    const done = (locked: boolean): void => { onLockChange({ ...state, locked }); reset(); };
    const call = mode === 'remove'
      ? emberApi.removeNotebookLock({ current_password: current }).then(() => done(false))
      : emberApi.setNotebookLock({ password: draft, current_password: current, source: 'change' })
        .then(() => done(true));
    call.catch(onFailure).finally(() => setBusy(false));
  };

  if (mode) {
    return (
      <div className="notebook-lock-row">
        <form className="notebook-form notebook-form-stack" onSubmit={submit}>
          <input
            className="notebook-input"
            type="password"
            value={current}
            onChange={(e) => { setCurrent(e.target.value); setError(''); }}
            placeholder="Current password"
            autoComplete="current-password"
            aria-label="Current notebook password"
          />
          {mode === 'change' && (
            <input
              className="notebook-input"
              type="password"
              value={draft}
              onChange={(e) => { setDraft(e.target.value); setError(''); }}
              placeholder="New password"
              autoComplete="new-password"
              aria-label="New notebook password"
            />
          )}
          <div className="notebook-form-actions">
            <button
              type="button"
              className="ember-text-action"
              onClick={reset}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="ember-btn ember-btn-primary"
              disabled={busy || !current.trim() || (mode === 'change' && !draft.trim())}
              data-log={mode === 'remove' ? 'notebook_lock_removed' : 'notebook_lock_set'}
              data-log-state={mode}
            >
              {mode === 'remove' ? 'Take it off' : 'Set it'}
            </button>
          </div>
        </form>
        {error && <p className="notebook-error" role="alert">{error}</p>}
        <p className="notebook-lock-help">
          The password keeps this page closed on a shared phone. Your Embers are
          kept by your account either way.
        </p>
      </div>
    );
  }

  return (
    <div className="notebook-lock-row">
      <button
        type="button"
        className="notebook-lock-action ember-text-action"
        onClick={() => setMode('change')}
      >
        Change the password
      </button>
      <button
        type="button"
        className="notebook-lock-action ember-text-action"
        onClick={() => setMode('remove')}
      >
        Take the lock off
      </button>
    </div>
  );
}
